import { useState, useEffect } from "react";
import axios from "../../plugins/axios";
import {
    Box,
    Button,
    TextField,
    Typography,
    Divider,
    Card,
    CardContent,
    Autocomplete,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import DocumentsTable, { type DocumentsTableAccount } from "./DocumentsTable";
import { useAuth } from "../../contexts/authContext";

interface AccountOption {
    id: number | string;
    text: string;
    value: number | string;
}

const useStyles = makeStyles()((theme) => ({
    title: {
        marginRight: theme.spacing(6),
    },
    selectAccountContainer: {
        margin: "0 auto",
        padding: theme.spacing(2),
        width: "100%",
        [theme.breakpoints.up("md")]: {
            width: 400,
        },
    },
    documentsTableContainer: {
        margin: "0 auto",
        width: "75%",
        padding: theme.spacing(4),
    },
}));

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

const Documents = () => {
    const { classes } = useStyles();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<AccountOption[]>([]);
    const [input, setInput] = useState("");
    const [selectedAccount, setSelectedAccount] = useState<DocumentsTableAccount | null>(null);

    useEffect(() => {
        let active = true;

        if (!input || input.length < 3) {
            setOptions([]);
            return undefined;
        }

        (async () => {
            const clients = (await axios.get<AccountOption[]>(
                `${apiUrl}/getclients/` + encodeURIComponent(input),
            )).data;
            if (active) setOptions(clients);
        })();

        return () => {
            active = false;
        };
    }, [open, input]);

    const handleAccountChange = (
        _e: React.SyntheticEvent,
        v: AccountOption | string | null,
    ) => {
        if (v && typeof v === "object") {
            setSelectedAccount({ accountId: v.id, accountName: v.text });
        } else {
            setSelectedAccount(null);
        }
    };

    const selectAccountComponent = () => (
        <Card className={classes.selectAccountContainer}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Select an account
                </Typography>
            </CardContent>
            <Box py={4} px={6}>
                <Autocomplete<AccountOption, false, false, true>
                    freeSolo
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    isOptionEqualToValue={(option, value) =>
                        typeof option !== "string" &&
                        typeof value !== "string" &&
                        option.value === value.value
                    }
                    getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.text
                    }
                    renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                            <li key={option.value} {...rest}>
                                {option.text}
                            </li>
                        );
                    }}
                    onChange={handleAccountChange}
                    options={options}
                    onInputChange={(_e, val) => setInput(val)}
                    renderInput={(params) => {
                        if (params.inputProps) {
                            params.inputProps.autoComplete = "account-documents";
                        }
                        return <TextField {...params} fullWidth />;
                    }}
                />
            </Box>
        </Card>
    );

    if (!user) return null;

    return (
        <Box display="flex" flexDirection="column" my={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" py={3}>
                <Typography variant="h3" className={classes.title}>
                    Documents - {user.details.firstName} {user.details.lastName}
                </Typography>
                {selectedAccount && (
                    <Button variant="outlined" onClick={() => setSelectedAccount(null)}>
                        Select different account
                    </Button>
                )}
            </Box>

            <Divider />

            {!selectedAccount || !selectedAccount.accountId ? (
                selectAccountComponent()
            ) : (
                <DocumentsTable account={selectedAccount} />
            )}
        </Box>
    );
};

export default Documents;
